import { Calculator, CalendarDays, ChevronDown, Download, Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { IMPORT_STATUS_LABEL } from '@obraplan/core/domain/schedule/schedule-import-status';
import type {
  ScheduleColumnMapping,
  ScheduleImportOptions,
} from '@obraplan/core/domain/schedule/schedule-import';
import type {
  ConfirmImportResult,
  ImportView,
  ValidatedImport,
} from '@obraplan/core/application/use-cases/schedule/schedule-import-use-cases';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { GenerateMaterialsFromTakeoffDialog } from '@/features/purchase-lines/GenerateMaterialsFromTakeoffDialog';
import { MaterialsTimeline } from '@/features/purchase-lines/MaterialsTimeline';
import { PurchaseLinesImportWizard } from '@/features/purchase-lines/PurchaseLinesImportWizard';
import { deadlineOf, summarizeMaterials } from '@/features/purchase-lines/materials-timeline';
import {
  NO_PURCHASE_FILTER,
  buildFilterOptions,
  changeFilter,
  describeFilter,
  isFiltered,
  toListInput,
  type PurchaseFilter,
} from '@/features/purchase-lines/purchase-filter';
import {
  EXPORT_PAGE_SIZE,
  fetchAllPurchaseLines,
  listaDeCompraEmCsv,
  nomeDoArquivoDeCompra,
} from '@/features/purchase-lines/purchase-export';
import { plural } from '@/features/purchase-lines/purchase-model';
import { materiaisDaObra } from '@/features/purchase-lines/takeoff-materials';
import { CronogramaHeader } from '@/features/schedule/CronogramaHeader';
import { ScheduleImportWizard } from '@/features/schedule/ScheduleImportWizard';
import { formatPeriod } from '@/features/schedule-items/schedule-items-model';
import { useQuantitativoMontado } from '@/features/schedule-items/use-takeoff-lines';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { baixarCsv } from '@/features/takeoff/exportar-quantitativo';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

const SELECT_CLASS =
  'h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-3 text-sm md:h-9';

const CHIP_CLASS =
  'rounded-full border px-3 py-1 text-xs transition-colors aria-pressed:border-(--color-brand) aria-pressed:bg-(--color-brand-soft) aria-pressed:text-(--color-content) border-(--color-border-subtle) bg-(--color-surface-raised) text-(--color-content-muted) hover:text-(--color-content)';

/**
 * A vista de materiais do Cronograma — o mesmo item da árvore de estrutura, visto pela
 * pergunta de suprimentos: o que precisa chegar, em que quantidade e quando.
 *
 * Desde a Fase 26 a linha é o **item de compra**: o material (ou o terceirizado) de um
 * serviço de uma frente, com a data que segue o item do cronograma por mão de obra. No
 * redesenho de 21/10/2026 a lista virou linha do tempo: o ritmo de chegada por semana em
 * cima, e as linhas por semana de necessidade embaixo. Para isso a tela lê **todas** as
 * linhas do filtro (`ListPurchaseLines`, em páginas de 500), em vez de uma página de cem: a
 * semana precisa da obra inteira para contar, e a lista de compra em CSV sai do mesmo
 * carregamento.
 */
export function SchedulePage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const today = useMemo(() => container.clock.today('America/Sao_Paulo').toString(), [container]);
  const canImport = usePermission(PERMISSION.SCHEDULE_IMPORT);
  // Gerar do quantitativo (Fase 35) lê o quantitativo: sem `takeoff.view`, o botão não aparece.
  const canReadTakeoff = usePermission(PERMISSION.TAKEOFF_VIEW);

  const [filter, setFilter] = useState<PurchaseFilter>(NO_PURCHASE_FILTER);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [onlyProvisional, setOnlyProvisional] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [purchaseWizardOpen, setPurchaseWizardOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  // A leitura mais cara da obra: só quando o "Gerar do quantitativo" abre.
  const takeoffMaterials = useQuantitativoMontado(generateOpen, materiaisDaObra);
  const [started, setStarted] = useState<ImportView | undefined>(undefined);
  const [validated, setValidated] = useState<ValidatedImport | undefined>(undefined);
  const [result, setResult] = useState<ConfirmImportResult | undefined>(undefined);

  // A lista: o pedido é o filtro, e a resposta leva a chave do que respondeu — trocar de
  // filtro mantém a lista antiga na tela, esmaecida, até a nova chegar.
  const requestKey = [filter.towerId, filter.floorId, filter.disciplineId, filter.serviceId].join(
    '|',
  );
  const answer = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : fetchAllPurchaseLines((page) =>
            container.listPurchaseLines.execute(
              toListInput(activeProjectId, filter, page, EXPORT_PAGE_SIZE),
              session,
            ),
          ).then((outcome) => ({ key: requestKey, outcome })),
    [session?.userId, activeProjectId, requestKey],
  );

  // As caixas de filtro precisam das torres, dos pavimentos e das frentes — não das linhas.
  // A árvore inteira (`GetProjectStructure`) traz cada linha de material com o material ao
  // lado, e com o orçamento importado passa de 10 MB a cada abertura e a cada escrita. A
  // lista não depende desta leitura, e a falta de permissão para ela não derruba a tela.
  const structure = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProjectOutline.execute({ projectId: activeProjectId }, session),
    [session?.userId, activeProjectId],
  );

  const services = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProjectServices.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  // Os avisos da obra dizem o que venceu e o que está no horizonte de alerta dela (RN-066):
  // é por eles que a linha ganha a cor, e não por um horizonte adivinhado na tela.
  const alerts = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getMaterialAlerts.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  const history = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.listScheduleImports.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  const towers = useMemo(
    () => (structure !== undefined && structure.ok ? structure.value.tree.towers : []),
    [structure],
  );
  const serviceList = useMemo(
    () => (services !== undefined && services.ok ? services.value : []),
    [services],
  );
  const options = useMemo(
    () => buildFilterOptions(towers, serviceList, filter),
    [towers, serviceList, filter],
  );
  const deadline = useMemo(
    () => deadlineOf(alerts !== undefined && alerts.ok ? alerts.value : undefined, today),
    [alerts, today],
  );

  const units = structure !== undefined && structure.ok ? structure.value.units : [];
  const current = answer !== undefined && answer.key === requestKey ? answer : undefined;
  const loaded = useMemo(
    () => (answer !== undefined && answer.outcome.ok ? answer.outcome.value : []),
    [answer],
  );
  // Os dois atalhos cortam na tela: o servidor já trouxe o filtro inteiro.
  const lines = useMemo(
    () =>
      loaded.filter(
        (line) =>
          (!onlyOverdue || deadline(line).level === 'OVERDUE') &&
          (!onlyProvisional || line.materialCodeProvisional),
      ),
    [loaded, onlyOverdue, onlyProvisional, deadline],
  );
  const summary = useMemo(() => summarizeMaterials(lines, deadline), [lines, deadline]);

  const updateFilter = (field: keyof PurchaseFilter, value: string): void => {
    setFilter((previous) => changeFilter(previous, field, value));
  };
  const clearFilter = (): void => {
    setFilter(NO_PURCHASE_FILTER);
    setOnlyOverdue(false);
    setOnlyProvisional(false);
  };

  // ── A lista de compra em CSV: do que já está carregado, com o mesmo filtro ──
  const exportCsv = (): void => {
    if (answer === undefined || !answer.outcome.ok) return;
    setExporting(true);
    try {
      baixarCsv(
        listaDeCompraEmCsv(lines),
        nomeDoArquivoDeCompra(describeFilter(filter, options), new Date()),
      );
      toast.success(`Lista de compra exportada: ${plural(lines.length, 'linha', 'linhas')}.`);
    } catch {
      toast.error('Não foi possível exportar a lista de compra.');
    } finally {
      setExporting(false);
    }
  };

  // ── Comandos do assistente da planilha do cronograma de materiais ───────────
  const [startImport, startState] = useCommand(
    (bytes: Uint8Array, fileName: string) =>
      container.startScheduleImport.execute(
        { projectId: activeProjectId!, bytes, fileName },
        session!,
      ),
    {
      silent: true,
      onSuccess: (value) => {
        setStarted(value);
        setValidated(undefined);
        setResult(undefined);
      },
    },
  );

  const [validateImport, validateState] = useCommand(
    (mapping: ScheduleColumnMapping, importOptions: ScheduleImportOptions) =>
      container.validateScheduleImport.execute(
        { importId: started?.record.id ?? '', mapping, options: importOptions },
        session!,
      ),
    { silent: true, onSuccess: (value) => setValidated(value) },
  );

  const [confirmImport, confirmState] = useCommand(
    () => container.confirmScheduleImport.execute(started?.record.id ?? '', session!),
    {
      successMessage: (value) =>
        `${String(value.createdEntries)} linhas criadas · ${String(value.updatedEntries)} atualizadas.`,
      silent: true,
      onSuccess: (value) => setResult(value),
    },
  );

  const [cancelImport] = useCommand(
    () => container.cancelScheduleImport.execute(started?.record.id ?? '', session!),
    { silent: true },
  );

  const closeWizard = (open: boolean): void => {
    setWizardOpen(open);
    if (open) return;
    // Fechar antes de confirmar descarta o staging: a planilha não fica pendurada.
    if (started !== undefined && result === undefined) void cancelImport();
    setStarted(undefined);
    setValidated(undefined);
    setResult(undefined);
  };

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para ver o cronograma."
        icon={<CalendarDays className="size-8" />}
      />
    );
  }

  if (answer === undefined) return <LoadingState label="Carregando o cronograma…" />;
  if (!answer.outcome.ok) {
    return <ErrorState message={answer.outcome.error.message} />;
  }

  const stale = current === undefined;
  const filtered = isFiltered(filter) || onlyOverdue || onlyProvisional;
  const nothingAtAll = loaded.length === 0 && !isFiltered(filter);

  return (
    <div className="flex flex-col gap-5">
      <CronogramaHeader
        atual="/cronograma"
        actions={
          <>
            {canImport && canReadTakeoff ? (
              <Button variant="primary" onClick={() => setGenerateOpen(true)}>
                <Calculator aria-hidden="true" />
                Gerar do quantitativo
              </Button>
            ) : canImport ? (
              <Button variant="primary" onClick={() => setPurchaseWizardOpen(true)}>
                <Upload aria-hidden="true" />
                Importar itens de compra
              </Button>
            ) : undefined}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary">
                  Mais
                  <ChevronDown aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  disabled={exporting || lines.length === 0}
                  onSelect={() => exportCsv()}
                >
                  <Download aria-hidden="true" />
                  {exporting ? 'Exportando…' : 'Exportar lista de compra'}
                </DropdownMenuItem>
                {canImport && (
                  <>
                    <DropdownMenuItem onSelect={() => setWizardOpen(true)}>
                      <Upload aria-hidden="true" />
                      Importar planilha
                    </DropdownMenuItem>
                    {canReadTakeoff && (
                      <DropdownMenuItem onSelect={() => setPurchaseWizardOpen(true)}>
                        <Upload aria-hidden="true" />
                        Importar itens de compra
                      </DropdownMenuItem>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {nothingAtAll ? (
        <EmptyState
          title="Cronograma vazio"
          description="Gere os materiais do quantitativo, importe a planilha de itens de compra ou lance itens pela árvore da estrutura."
          icon={<CalendarDays className="size-8" />}
          {...(canImport
            ? {
                action: {
                  label: 'Importar itens de compra',
                  onClick: () => setPurchaseWizardOpen(true),
                },
              }
            : {})}
        />
      ) : (
        <>
          <p className="text-sm text-(--color-content-muted)" data-testid="resumo-dos-materiais">
            <span className="font-semibold text-(--color-content)">
              {summary.total.toLocaleString('pt-BR')}{' '}
              {summary.total === 1 ? 'material a chegar' : 'materiais a chegar'}
              {filtered && ' neste filtro'}
            </span>
            {summary.first !== undefined && summary.last !== undefined && (
              <> · {formatPeriod(summary.first, summary.last)}</>
            )}
            {summary.overdue > 0 && (
              <>
                {' · '}
                <span className="font-semibold text-(--color-state-danger)">
                  {summary.overdue} {summary.overdue === 1 ? 'vencido' : 'vencidos'}
                </span>
              </>
            )}
            {summary.dueSoon > 0 && (
              <>
                {' · '}
                <span className="text-(--color-state-warn)">
                  {summary.dueSoon} no horizonte de alerta
                </span>
              </>
            )}
            {summary.provisional > 0 && <> · {summary.provisional} com código provisório</>}
            {summary.total > 0 &&
              (summary.fromTakeoff === summary.total
                ? ' · todos do quantitativo'
                : summary.fromTakeoff > 0 && ` · ${String(summary.fromTakeoff)} do quantitativo`)}
          </p>

          <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
            {towers.length > 0 && (
              <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-auto lg:grid-cols-4">
                <Field label="Torre" htmlFor="lines-tower">
                  <select
                    id="lines-tower"
                    className={SELECT_CLASS}
                    value={filter.towerId}
                    onChange={(event) => updateFilter('towerId', event.target.value)}
                  >
                    <option value="">Todas as torres</option>
                    {options.towers.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Pavimento" htmlFor="lines-floor">
                  <select
                    id="lines-floor"
                    className={SELECT_CLASS}
                    value={filter.floorId}
                    onChange={(event) => updateFilter('floorId', event.target.value)}
                  >
                    <option value="">Todos os pavimentos</option>
                    {options.floors.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Disciplina" htmlFor="lines-discipline">
                  <select
                    id="lines-discipline"
                    className={SELECT_CLASS}
                    value={filter.disciplineId}
                    onChange={(event) => updateFilter('disciplineId', event.target.value)}
                  >
                    <option value="">Todas as disciplinas</option>
                    {options.disciplines.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
                {serviceList.length > 0 && (
                  <Field label="Serviço" htmlFor="lines-service">
                    <select
                      id="lines-service"
                      className={SELECT_CLASS}
                      value={filter.serviceId}
                      onChange={(event) => updateFilter('serviceId', event.target.value)}
                    >
                      <option value="">Todos os serviços</option>
                      {options.services.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 pb-0.5">
              <button
                type="button"
                aria-pressed={onlyOverdue}
                onClick={() => setOnlyOverdue((value) => !value)}
                className={cn(CHIP_CLASS)}
              >
                Só vencidos
              </button>
              <button
                type="button"
                aria-pressed={onlyProvisional}
                onClick={() => setOnlyProvisional((value) => !value)}
                className={cn(CHIP_CLASS)}
              >
                Só código provisório
              </button>
            </div>
          </div>

          {lines.length === 0 ? (
            <EmptyState
              title="Nada neste filtro"
              description="Nenhuma linha corresponde à torre, ao pavimento, à disciplina, ao serviço e aos atalhos escolhidos."
              icon={<CalendarDays className="size-8" />}
              action={{ label: 'Limpar filtros', onClick: clearFilter }}
            />
          ) : (
            <MaterialsTimeline lines={lines} today={today} deadline={deadline} stale={stale} />
          )}

          <p className="max-w-[78ch] text-xs text-(--color-content-muted)">
            Vencido é o material cuja data de necessidade já passou. O ObraPlan não acompanha pedido
            nem entrega: o aviso existe para a conversa com compras acontecer antes de a frente
            parar.
          </p>
        </>
      )}

      {history !== undefined && history.ok && history.value.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Importações</CardTitle>
            <CardDescription>De onde veio cada linha — o arquivo não é guardado.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <TableWrapper>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quando</TableHead>
                    <TableHead>Arquivo</TableHead>
                    <TableHead>Situação</TableHead>
                    <TableHead className="text-right">Linhas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.value.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="tabular text-xs">
                        {formatDateTime(record.createdAt)}
                      </TableCell>
                      <TableCell className="max-w-64 truncate">{record.fileName}</TableCell>
                      <TableCell>
                        <Badge tone={record.status === 'CONFIRMED' ? 'ok' : 'neutral'}>
                          {IMPORT_STATUS_LABEL[record.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs text-(--color-content-muted)">
                        {record.status === 'CONFIRMED'
                          ? `${String(record.createdEntries)} criadas · ${String(record.updatedEntries)} atualizadas`
                          : `${String(record.totalRows)} lidas`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          </CardContent>
        </Card>
      )}

      <ScheduleImportWizard
        open={wizardOpen}
        onOpenChange={closeWizard}
        units={units}
        started={started}
        validated={validated}
        result={result}
        onFile={(file) => {
          void file.arrayBuffer().then((buffer) => {
            void startImport(new Uint8Array(buffer), file.name);
          });
        }}
        onValidate={(mapping, importOptions) => void validateImport(mapping, importOptions)}
        onConfirm={() => void confirmImport()}
        isPending={startState.isPending || validateState.isPending || confirmState.isPending}
        errorMessage={
          startState.error?.message ?? validateState.error?.message ?? confirmState.error?.message
        }
      />

      {canImport && (
        <PurchaseLinesImportWizard open={purchaseWizardOpen} onOpenChange={setPurchaseWizardOpen} />
      )}
      {canImport && canReadTakeoff && (
        <GenerateMaterialsFromTakeoffDialog
          open={generateOpen}
          onOpenChange={setGenerateOpen}
          takeoff={takeoffMaterials}
        />
      )}
    </div>
  );
}
